import React, { useMemo } from 'react';
import { useAtom } from 'jotai/index';
import styles from './css/index.module.scss';
import { configuratorGlassTypesAtom, selectedLayerAtom } from '../../../../atoms/configuratorAtoms';
import Select from '../../../../ui-kit/Select';
import Button from '../../../../ui-kit/Button';
import useTranslations from '../../../../hooks/useTranslations';
import { DICTIONARY } from '../../../../translations/dictionary';
import EmptyHeight from '../../../../components/EmptyHeight';
import UnderlinedButton from '../../../../ui-kit/UnderlinedButton';
import useTutorials from '../../../../hooks/useTutorials';
import { materialsAtom } from '../../../../atoms/categoriesAtoms';
import { MATERIALS_IDS } from '../../../../constants/configurator';
import ConfiguratorServices from '../../../../services/ConfiguratorServices';

function SelectItem({
  param, selectedOptions = [], setSelectedLayer, selectedLayer, changeTutorialStep, tutorialStep, isInvalid,
}) {
  const options = useMemo(() => param.options.map((i) => ({
    id: i.id, name: i.name + (param.unit ? ` ${param.unit}` : ''),
  })), [param]);

  const selectedVal = useMemo(() => {
    const selected = options.find((i) => selectedOptions.includes(i.id));
    return selected || null;
  }, [options, selectedOptions]);

  const handleChange = (val) => {
    const optionsOfThisParam = param.options.map((i) => i.id);
    const newSelectedOptions = selectedOptions
      .filter((i) => !optionsOfThisParam.includes(i)); // removing selected option of this param
    newSelectedOptions.push(val.id); // adding new option of this param;

    const newSelectedLayer = {
      ...selectedLayer,
      material: { ...selectedLayer.material, selected_options: newSelectedOptions },
    };
    setSelectedLayer(newSelectedLayer);

    if (tutorialStep === 6) {
      changeTutorialStep(val);
    }
  };

  const handleFocus = () => {
    if (tutorialStep === 5) {
      changeTutorialStep(param);
    }
  };

  return (
    <Select
      text={param.description}
      value={selectedVal}
      onChange={handleChange}
      onInputFocus={handleFocus}
      items={options}
      valueField="id"
      labelField="name"
      width="100%"
      closeOnClickOutside={![5, 6].includes(tutorialStep)}
      searchable={![5, 6].includes(tutorialStep)}
      error={isInvalid ? 'NO_ERROR_TEXT' : undefined}
      titleClassName={styles.selectLabel}
    />
  );
}

function LayerOptionsModal({ onSave, onRemove }) {
  const { t } = useTranslations();
  const { changeTutorialStep, tutorialStep } = useTutorials();
  const [selectedLayer, setSelectedLayer] = useAtom(selectedLayerAtom);
  const [materials] = useAtom(materialsAtom);
  const [baseGlasses] = useAtom(configuratorGlassTypesAtom);

  const isGlass = selectedLayer?.material?.material_type_id === MATERIALS_IDS.glass;

  const glassOptions = useMemo(
    () => ConfiguratorServices.getSelectableGlasses(baseGlasses),
    [baseGlasses],
  );

  const isBaseGlass = isGlass && glassOptions.some((glass) => glass.id === selectedLayer?.material?.id);

  const selectedGlass = useMemo(() => (
    glassOptions.find((glass) => glass.id === selectedLayer?.material?.id) || null
  ), [glassOptions, selectedLayer?.material?.id]);

  const glassSelectItems = useMemo(
    () => glassOptions.filter((glass) => glass.id !== selectedLayer?.material?.id),
    [glassOptions, selectedLayer?.material?.id],
  );

  const invalidParamKeys = useMemo(() => {
    if (!isBaseGlass) return new Set();

    const selectedOptions = selectedLayer.material.selected_options || [];
    const keys = [];
    (selectedLayer.material.input_params || []).forEach((param, index) => {
      const isMissing = param.options?.length > 0
        && !param.options.some((option) => selectedOptions.includes(option.id));
      if (isMissing) keys.push(param.id ?? param.name ?? index);
    });
    return new Set(keys);
  }, [isBaseGlass, selectedLayer?.material]);

  const onGlassChange = (glass) => {
    if (!glass || glass.id === selectedLayer.material.id) return;

    const material = ConfiguratorServices.replaceGlassMaterial({
      currentMaterial: selectedLayer.material,
      nextMaterialId: glass.id,
      materials,
    });

    setSelectedLayer({
      ...selectedLayer,
      material,
    });
  };

  const onSaveClick = () => {
    if (invalidParamKeys.size) return;

    onSave(selectedLayer);

    if (tutorialStep === 7) {
      changeTutorialStep(selectedLayer.material);
    }
  };
  const onDeleteClick = () => {
    onRemove(selectedLayer);
  };

  if (!selectedLayer) return null;
  return (
    <div className={styles.layerOptionsModal}>
      {isBaseGlass ? (
        <div className={styles.glassSelect}>
          <Select
            text={t(DICTIONARY.glassType)}
            value={selectedGlass}
            onChange={onGlassChange}
            items={glassSelectItems}
            valueField="id"
            labelField="name"
            width="100%"
            searchable={false}
            titleClassName={styles.selectLabel}
          />
        </div>
      ) : (
        <p className={styles.title}>{selectedLayer.material.name}</p>
      )}
      <div className={styles.selectsList}>
        {(selectedLayer.material.input_params || []).map((param, index) => (
          <SelectItem
            key={param.id ?? param.name ?? index}
            param={param}
            selectedOptions={selectedLayer.material.selected_options}
            selectedLayer={selectedLayer}
            setSelectedLayer={setSelectedLayer}
            changeTutorialStep={changeTutorialStep}
            tutorialStep={tutorialStep}
            isInvalid={invalidParamKeys.has(param.id ?? param.name ?? index)}
          />
        ))}
      </div>

      <div className={styles.buttons}>
        {selectedLayer.isNewLayer ? (
          <Button width="100%" text={t(DICTIONARY.add)} onClick={onSaveClick} isDisabled={invalidParamKeys.size > 0} />
        ) : (
          <>
            {(isBaseGlass || !!selectedLayer.material.input_params?.length) && (
              <Button width="100%" text={t(DICTIONARY.edit)} onClick={onSaveClick} isDisabled={invalidParamKeys.size > 0} />
            )}
            <UnderlinedButton text={t(DICTIONARY.delete)} onClick={onDeleteClick} />
          </>
        )}
      </div>
    </div>
  );
}

export default LayerOptionsModal;
